import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { LogIn, Mail, AlertCircle } from "lucide-react";
import { SiReplit, SiGoogle, SiGithub } from "react-icons/si";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function LoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");

  /** Returns the safe return-to path from the URL, falling back to "/". */
  const getReturnTo = (): string => {
    const val = new URLSearchParams(window.location.search).get("returnTo");
    // Accept only relative same-origin paths; strip anything that looks like a URL.
    return val && /^\/[^/]/.test(val) ? val : "/";
  };

  const handleReplitLogin = () => {
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    window.location.href = returnTo ? `/api/login?returnTo=${encodeURIComponent(returnTo)}` : "/api/login";
  };

  const handleDevLogin = async (role: 'owner' | 'admin' | 'user') => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/dev/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });

      if (response.ok) {
        window.location.href = "/";
      } else {
      }
    } catch (error) {
    } finally{
      setIsLoading(false);
    }
  };

  const handleUsernamePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      setLoginError("Please enter both username and password");
      return;
    }

    setIsLoading(true);
    setLoginError("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        const returnTo = getReturnTo();
        // Check if user needs to reset password
        if (data.user?.needsPasswordReset) {
          window.location.href = "/reset-password";
        } else if (data.user?.requiresTwoFactor && !data.user?.twoFactorVerified) {
          // Keep a pending assistant out of the application shell entirely.
          // Carry the returnTo through so after 2FA the user lands on the right page.
          const twoFactorDest = returnTo !== "/" ? `/two-factor?returnTo=${encodeURIComponent(returnTo)}` : "/two-factor";
          window.location.href = twoFactorDest;
        } else {
          // An assistant with no explicit destination should see assignments
          // immediately, instead of landing on a dashboard with no task list.
          window.location.href = data.user?.requiresTwoFactor && returnTo === "/" ? "/tasks" : returnTo;
        }
      } else {
        const data = await response.json();
        setLoginError(data.error || data.message || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      setLoginError("An error occurred. Please try again.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email or username");
      return;
    }

    setIsForgotPasswordLoading(true);
    setForgotPasswordError("");
    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrUsername: forgotPasswordEmail }),
      });

      if (response.ok) {
        setForgotPasswordSuccess(true);
      } else {
        const data = await response.json();
        setForgotPasswordError(data.error || "Failed to send reset email. Please try again.");
      }
    } catch (error) {
      setForgotPasswordError("An error occurred. Please try again.");
      console.error("Forgot password error:", error);
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSuccess(false);
    setForgotPasswordError("");
    setLoginError("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-login" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {showForgotPassword ? "Reset Password" : "Log In"}
          </DialogTitle>
          <DialogDescription>
            {showForgotPassword 
              ? "Enter your email or username to receive a password reset link" 
              : "Choose your preferred login method"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showForgotPassword ? (
            // Forgot Password Form
            <>
              {forgotPasswordSuccess ? (
                <div className="space-y-4">
                  <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
                    <p className="text-sm text-green-800 dark:text-green-200" data-testid="text-forgot-password-success">
                      Password reset email sent. Please check your inbox.
                    </p>
                  </div>
                  <Button
                    onClick={handleBackToLogin}
                    variant="outline"
                    className="w-full"
                    data-testid="button-back-to-login"
                  >
                    Back to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {forgotPasswordError && (
                    <Alert variant="destructive" data-testid="alert-forgot-password-error">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{forgotPasswordError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email or Username</Label>
                    <Input
                      id="forgot-email"
                      data-testid="input-forgot-password-email"
                      type="text"
                      placeholder="Enter your email or username"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      disabled={isForgotPasswordLoading}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isForgotPasswordLoading}
                    data-testid="button-forgot-password-submit"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {isForgotPasswordLoading ? "Sending..." : "Send Reset Email"}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleBackToLogin}
                    variant="ghost"
                    className="w-full"
                    data-testid="button-back-to-login"
                  >
                    Back to Login
                  </Button>
                </form>
              )}
            </>
          ) : (
            // Login Form
            <>
              {/* Development Quick Login (only shown in dev mode) */}
              {import.meta.env.DEV && (
                <>
                  <div className="space-y-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                      Development Quick Login
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        onClick={() => handleDevLogin('owner')}
                        className="w-full"
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        data-testid="button-dev-login-owner"
                      >
                        Owner
                      </Button>
                      <Button
                        onClick={() => handleDevLogin('admin')}
                        className="w-full"
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        data-testid="button-dev-login-admin"
                      >
                        Admin
                      </Button>
                      <Button
                        onClick={() => handleDevLogin('user')}
                        className="w-full"
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        data-testid="button-dev-login-user"
                      >
                        User
                      </Button>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Replit OAuth / SSO Login */}
              <div className="space-y-3">
                <Button
                  onClick={handleReplitLogin}
                  className="w-full h-12"
                  variant="default"
                  data-testid="button-login-replit"
                >
                  <SiReplit className="w-5 h-5 mr-2" />
                  Sign in with Replit
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Includes Google, GitHub, and other SSO options
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              {/* Username/Password Login */}
              <form onSubmit={handleUsernamePasswordLogin} className="space-y-4">
                {loginError && (
                  <Alert variant="destructive" data-testid="alert-login-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="input-username"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="input-password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:underline"
                      data-testid="link-forgot-password"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="button-login-submit"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  {isLoading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
